import { useContext } from 'react';
import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkFilled, Dismiss24Regular, HatGraduation20Regular } from '@fluentui/react-icons';

import { SignInContext } from '@modules/users';
import { ToastContext } from '@shared/contexts';
import { ButtonLoading } from '@shared/components';

import useTrainingStore from '../stores/useTrainingStore';

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  documentId: string;
  documentName: string;
  versionId: string;
};

const useStyles = makeStyles({
  statement: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  icon: {
    flexShrink: 0,
    fontSize: '20px',
    color: tokens.colorBrandForeground1,
  },
});

// The document-overview confirmation for an assigned training. Confirming records
// the completion and deactivates the document's training surfaces.
const ConfirmTrainingDialog = ({ isOpen, setIsOpen, documentId, documentName, versionId }: TProps) => {
  const classes = useStyles();
  const { user } = useContext(SignInContext);
  const { show } = useContext(ToastContext);
  const complete = useTrainingStore((state) => state.complete);
  const deactivate = useTrainingStore((state) => state.deactivate);

  const handleConfirm = () => {
    complete({ completedBy: user.name || user.mail });
    deactivate(documentId);
    show('success', 'Training completed.');
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Confirm understanding
          </DialogTitle>
          <DialogContent>
            <div className={classes.statement}>
              <HatGraduation20Regular className={classes.icon} />
              <Body1>
                I confirm that I have read and understood version {versionId} of {documentName}
              </Body1>
            </div>
          </DialogContent>
          <DialogActions>
            <ButtonLoading appearance="primary" icon={<CheckmarkFilled />} onClick={handleConfirm}>
              Confirm
            </ButtonLoading>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ConfirmTrainingDialog;
